import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SessionBaseComponent } from '../../session.base.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { LaasOption } from 'src/app/core/models/laas-option';
import {
  deepCopy,
  getFacilityByPermission,
  isNullOrEmpty,
} from 'src/app/core/utils/common-functions';
import { UserType } from 'src/app/core/enums/user-type.enum';
import { LogisticsFacilityService } from 'src/app/core/services/logistics/facility/logistics-facility.service';
import { PermissionService } from 'src/app/core/services/permission/permission.service';


@Component({
  selector: 'laas-facility-dropdown',
  templateUrl: './facility-dropdown.component.html',
  styleUrls: ['./facility-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FacilityDropdownComponent),
      multi: true
    }
  ]
})
export class FacilityDropdownComponent
  extends SessionBaseComponent
  implements OnInit, ControlValueAccessor
{
  @Input() divClass: string = '';
  @Input() labelClass: string = '';
  @Input() dropdownStyle: any = {};
  @Input() showLabel: boolean = false;
  @Input() showClear: boolean = true;
  @Input() showFilter: boolean = true;
  @Input() defaultSelectFirst: boolean = false;
  @Input() permissionCode!: PermissionConstants;
  @Input() isSingle: boolean = false;
  @Input() isMultiple: boolean = false;
  @Input() formControlName: string = '';
  @Input() showAllOptionItem: boolean = false;
  @Input() showToggleAll: boolean = false;
  @Input() facilityDataList: any[] = [];
  @Input() facilityData!: any;
  @Input() isIcon: boolean = false;

  @Output() onOptionDataChange: EventEmitter<any> = new EventEmitter();

  // ControlValueAccessor 相關屬性
  private _value: any;
  private _onChange = (value: any) => {};
  private _onTouched = () => {};
  disabled = false;

  virtualScroll: boolean = false;
  facilityOptions: LaasOption[] = [];
  facilityOptionsByPermission: LaasOption[] = [];
  allOptionItem: LaasOption = {
    value: 'ALL',
    label: 'ALL - 全部',
  };

  constructor(
    injector: Injector,
    private logisticsFacilityService: LogisticsFacilityService,
    private permissionService: PermissionService
  ) {
    super(injector);
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    this.logisticsFacilityService.getFacilityOptions().subscribe({
      next: (result: any[]) => {
        this.facilityOptions = deepCopy(result) ?? [];

        // 確保白名單已載入後再組裝選單
        this.permissionService.ensureLoaded().subscribe(() => {
          this.buildFacilityOptions(result);
        });
      },
    });
  }

  /**
   * 白名單載入完成後，依權限過濾並組裝 facility 下拉選項
   */
  private buildFacilityOptions(result: any[]): void {
    // Base 權限 (by role)：依 permissionCode 篩出擁有該功能的 role 的倉別，維持功能級隔離
    const facilityByPermission = getFacilityByPermission(
      this.userProfile,
      this.permissionCode
    );
    const isAllNull = facilityByPermission.length > 0 && facilityByPermission.every(isNullOrEmpty);

    // 白名單擴充 (by user)：有傳入 permissionCode 時，查該功能額外開放的倉別
    const whitelistFacilities = this.permissionCode
      ? this.permissionService.getWhitelistValues(this.permissionCode, 'Facility')
      : [];

    const facilityOptionsByPermission = result.filter(
      (x: any) =>
        (this?.userProfile?.userType === UserType.SuperAdmin.toString()) ||
        isAllNull ||
        facilityByPermission.includes(x.value) ||
        whitelistFacilities.includes(x.value)
    );
    this.facilityOptionsByPermission = facilityOptionsByPermission.map(
      (x: any) => ({ value: x.value, label: x.label })
    );
    if (this.showAllOptionItem) {
      this.facilityOptionsByPermission.unshift(this.allOptionItem);
    }
    this.virtualScroll =
      this.facilityOptionsByPermission &&
      this.facilityOptionsByPermission.length > 30;
    this.changeDefaultSelectFirst();
  }

  changeDefaultSelectFirst(): void {
    if ((this.isSingle || this.isIcon) && this.defaultSelectFirst) {
      if (this.facilityOptionsByPermission.length > 0) {
        if (
          isNullOrEmpty(this.laasSetting.facility) ||
          !this.facilityOptionsByPermission
            .map((x) => x.value)
            .includes(this.laasSetting.facility)
        ) {
          this.facilityData = this.facilityOptionsByPermission[0].value;
          this.laasSetting.facility = this.facilityData;
          // 通知表單控制項值的變化
          this._value = this.facilityData;
          this._onChange(this.facilityData);
        } else {
          this.facilityData = this.laasSetting.facility;
          // 通知表單控制項值的變化
          this._value = this.facilityData;
          this._onChange(this.facilityData);
        }
      }
    }
  }

  async onChange(event: any): Promise<void> {
    this._value = event;
    this._onChange(event);
    this._onTouched();
    this.onOptionDataChange.emit(event);
  }

  // ControlValueAccessor 實現
  writeValue(value: any): void {
    this._value = value;
    if (this.isMultiple) {
      this.facilityDataList = value || [];
    } else {
      this.facilityData = value;
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // 取得當前值的 getter
  get value(): any {
    return this._value;
  }
}
